/**
 * 2XU Wishlist — Relewise-backed favorites.
 *
 * Two responsibilities, both opt-in based on context:
 *
 * 1. On every page: bind every [data-wishlist-btn] (in light DOM and
 *    inside Relewise web-component shadow roots) to call Relewise's
 *    User Engagement API. Hydrate filled/outline state on load.
 *
 * 2. On /pages/wishlist: render the user's favorites as full
 *    tabbed-product-card markup inside #WishlistGrid via a
 *    ProductSearch with engagement filter — including the
 *    hover-size add-to-cart overlay used on collection pages.
 *
 * Required globals:
 *   - window.RELEWISE_CONFIG (datasetId, apiKey, serverUrl) — emitted
 *     by theme.liquid from theme settings.
 *   - window.ShopifyRelewise.getUserAsync() — provided by the Relewise
 *     Shopify app embed.
 *
 * Anonymous shoppers (no consent / not logged in) get the empty state
 * on the wishlist page and inert hearts elsewhere.
 *
 * Consumption gating: the favorites lookup is a billable Relewise
 * ProductSearchRequest, and this module runs on EVERY page — including
 * renders by JS-executing crawlers, which vastly outnumber human
 * sessions and always start from a fresh browser context. So the
 * lookup only fires when it can actually matter:
 *   - on /pages/wishlist (always fresh — the page needs full data),
 *   - on devices that have used hearts before (localStorage flag,
 *     set on first toggle),
 *   - for logged-in customers (authenticatedId → cross-device
 *     favorites; logged-in traffic is human).
 * Everyone else — including every bot render — fires ZERO favorites
 * searches; their hearts correctly render in the empty/outline state.
 * Results are cached in sessionStorage so returning wishlist users
 * cost at most one search per browsing session, refreshed locally on
 * each toggle.
 */

import {
  Searcher,
  Tracker,
  ProductSearchBuilder,
} from 'https://esm.sh/@relewise/client';

// Flip to true (or detect ?debug=wishlist) to re-enable operational logs.
const DEBUG = false;
function debug(...args) { if (DEBUG) console.log(...args); }

debug('[Wishlist] module loaded');

/* ────────────────── State ────────────────── */

const cfg = window.RELEWISE_CONFIG;
const grid = document.getElementById('WishlistGrid');
const empty = document.getElementById('WishlistEmpty');
const countEl = document.getElementById('WishlistCount');
const toolbarEl = document.getElementById('WishlistToolbar');
const sortEl = document.getElementById('WishlistSort');
const shareBtn = document.getElementById('WishlistShare');
const clearBtn = document.getElementById('WishlistClear');
const toastEl = document.getElementById('WishlistToast');
const headerCountEl = document.getElementById('wishlist-count-bubble');

let searcher;
let tracker;
let settings;
let formatter;
let currentUser = null;
const favoriteIds = new Set();        // Shopify numeric product IDs that are favorited
const favoriteHandles = new Set();    // same set, keyed by handle (parsed from Relewise Url data)
const handleToId = new Map();         // handle → id cache, persisted to localStorage so reloads keep state
let hydrateTimer = null;

// Wishlist-page cache: keep the rendered cards in memory so we can
// re-sort without re-fetching every product from Shopify.
const wishlistCards = []; // [{ id, title, price, addedAt, html, handle }]

const HANDLE_CACHE_KEY = '2xu:wishlist:handle-to-id';
const ADDED_AT_KEY = '2xu:wishlist:added-at';
// Set (to '1') the first time this device toggles a heart — marks it as a
// wishlist user whose hearts are worth hydrating on future page loads.
const WISHLIST_USER_KEY = '2xu:wishlist:active';
// Session-scoped favorites snapshot {ids: [], handles: []} so hydration on
// subsequent pages in the same session costs zero API calls.
const FAVORITES_CACHE_KEY = '2xu:wishlist:favorites';
const addedAtMap = loadAddedAt();
loadHandleCache();

if (!cfg?.datasetId || !cfg?.apiKey) {
  console.error(
    '[Wishlist] Missing window.RELEWISE_CONFIG. Set Dataset ID + API key in ' +
      'Theme Customize → Theme settings → Relewise.'
  );
  if (grid) showEmpty();
} else {
  bootstrap().catch((err) => console.error('[Wishlist] bootstrap failed', err));
}

/* ────────────────── Bootstrap ────────────────── */

async function bootstrap() {
  searcher = new Searcher(cfg.datasetId, cfg.apiKey, { serverUrl: cfg.serverUrl });
  tracker = new Tracker(cfg.datasetId, cfg.apiKey, { serverUrl: cfg.serverUrl });

  const language = grid?.dataset.language || document.documentElement.lang || 'en-US';
  const currency = grid?.dataset.currency || 'USD';
  settings = { language, currency, displayedAtLocation: 'Storefront' };
  formatter = new Intl.NumberFormat(language, { style: 'currency', currency });

  // Capture-phase delegation: this listener fires BEFORE any handler
  // attached inside Relewise's web-component shadow roots can call
  // stopPropagation. Without `true`, Lit-internal click handlers swallow
  // the event and we never see it at document.
  document.addEventListener('click', onHeartClick, true);
  debug('[Wishlist] click listener attached (capture phase)');

  if (grid) {
    bindToolbar();
    grid.addEventListener('click', onSizeButtonClick);
  }

  currentUser = await getUser();
  debug('[Wishlist] user', currentUser);

  if (!currentUser || isAnonymous(currentUser)) {
    console.warn('[Wishlist] Anonymous user — favorites disabled.');
    if (grid) showEmpty();
    return;
  }

  // Favorites hydration — gated so bot renders never fire the billable
  // favorites search (see module comment). Wishlist page always fetches
  // fresh; other pages use the session cache, or fetch once (deferred to
  // idle) only for known wishlist users / logged-in customers.
  if (grid) {
    await loadFavorites();
    debug('[Wishlist] favorites loaded (wishlist page)', favoriteIds.size);
    renderWishlistPage();
  } else if (loadFavoritesCache()) {
    debug('[Wishlist] favorites hydrated from session cache', favoriteIds.size);
  } else if (isWishlistUser() || currentUser.authenticatedId) {
    deferToIdle(() => {
      loadFavorites().then(() => {
        debug('[Wishlist] favorites loaded (deferred)', favoriteIds.size);
        hydrateAll();
      });
    });
  } else {
    debug('[Wishlist] no wishlist history on device — skipping favorites fetch');
  }

  // Hydrate hearts on the rest of the page (PLP, search overlay, etc.)
  hydrateAll();

  // Relewise renders cards async into shadow roots. Watch for new ones.
  const obs = new MutationObserver(scheduleHydrate);
  obs.observe(document.body, { childList: true, subtree: true });
}

function isWishlistUser() {
  try {
    return localStorage.getItem(WISHLIST_USER_KEY) === '1';
  } catch {
    return false;
  }
}

function markWishlistUser() {
  try {
    localStorage.setItem(WISHLIST_USER_KEY, '1');
  } catch {
    // Quota / privacy mode — fail silently.
  }
}

function deferToIdle(fn) {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(fn, { timeout: 3000 });
  } else {
    setTimeout(fn, 500);
  }
}

/* ────────────────── Favorites query ────────────────── */

async function loadFavorites() {
  try {
    const builder = new ProductSearchBuilder({ ...settings, user: currentUser })
      .setSelectedProductProperties({ allData: true })
      .pagination((p) => p.setPageSize(200).setPage(1))
      .filters((f) => f.addProductEngagementFilter({ isFavorite: true }));

    const response = await searcher.searchProducts(builder.build());
    const items = response?.results ?? [];
    if (items[0]) debug('[Wishlist] sample favorite item:', items[0]);
    favoriteIds.clear();
    favoriteHandles.clear();
    // Backfill stable addedAt timestamps for favorites that pre-date the
    // sort feature, using Relewise's return order so the sequence is at
    // least stable across reloads. 1ms decrement keeps newer client-side
    // adds (Date.now()) sorting ahead of all of these.
    const backfillBase = Date.now() - 24 * 60 * 60 * 1000;
    let backfillStep = 0;
    for (const item of items) {
      const id = String(item.productId);
      favoriteIds.add(id);
      const handle = extractHandle(item);
      if (handle) {
        favoriteHandles.add(handle);
        handleToId.set(handle, id);
      }
      if (!addedAtMap.has(id)) {
        addedAtMap.set(id, backfillBase - backfillStep++);
      }
    }
    // Bridge any IDs whose handle we know from the persisted cache.
    for (const id of favoriteIds) {
      for (const [h, mappedId] of handleToId) {
        if (String(mappedId) === id) favoriteHandles.add(h);
      }
    }
    persistHandleCache();
    persistAddedAt();
    persistFavoritesCache();
  } catch (err) {
    console.error('[Wishlist] Could not load favorites', err);
  }
}

/* Session cache of the favorites sets — lets every page after the first in
   a session hydrate hearts without a billable ProductSearchRequest. */

function loadFavoritesCache() {
  try {
    const raw = sessionStorage.getItem(FAVORITES_CACHE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.ids) || !Array.isArray(parsed.handles)) return false;
    favoriteIds.clear();
    favoriteHandles.clear();
    for (const id of parsed.ids) favoriteIds.add(String(id));
    for (const h of parsed.handles) {
      if (typeof h === 'string') favoriteHandles.add(h);
    }
    return true;
  } catch {
    return false;
  }
}

function persistFavoritesCache() {
  try {
    sessionStorage.setItem(
      FAVORITES_CACHE_KEY,
      JSON.stringify({ ids: [...favoriteIds], handles: [...favoriteHandles] })
    );
  } catch {
    // Quota / privacy mode — fail silently.
  }
}

function extractHandle(item) {
  // Look for a handle in any data field that holds a product URL.
  if (!item?.data) return null;
  for (const key of Object.keys(item.data)) {
    const v = readData(item, key);
    if (typeof v === 'string') {
      const h = handleFromUrl(v);
      if (h) return h;
    }
  }
  return null;
}

function handleFromUrl(url) {
  if (!url || typeof url !== 'string') return null;
  // Accept absolute or relative URLs ending in /products/<handle>[?...]
  const match = url.match(/\/products\/([^/?#]+)/);
  return match ? match[1] : null;
}

async function resolveProductId(btn) {
  const idAttr = btn.dataset.productId;
  if (idAttr) return idAttr;
  const handle = btn.dataset.productHandle;
  if (!handle) return null;
  if (handleToId.has(handle)) return handleToId.get(handle);
  try {
    const res = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const data = await res.json();
    const id = String(data.id);
    handleToId.set(handle, id);
    persistHandleCache();
    return id;
  } catch (err) {
    console.error('[Wishlist] Failed to resolve handle → id', handle, err);
    return null;
  }
}

function loadHandleCache() {
  try {
    const raw = localStorage.getItem(HANDLE_CACHE_KEY);
    if (!raw) return;
    const entries = JSON.parse(raw);
    if (!Array.isArray(entries)) return;
    for (const [h, id] of entries) {
      if (typeof h === 'string' && (typeof id === 'string' || typeof id === 'number')) {
        handleToId.set(h, String(id));
      }
    }
  } catch {
    // Ignore malformed cache.
  }
}

function persistHandleCache() {
  try {
    localStorage.setItem(HANDLE_CACHE_KEY, JSON.stringify([...handleToId]));
  } catch {
    // Quota / privacy mode — fail silently.
  }
}

function loadAddedAt() {
  const map = new Map();
  try {
    const raw = localStorage.getItem(ADDED_AT_KEY);
    if (!raw) return map;
    const entries = JSON.parse(raw);
    if (!Array.isArray(entries)) return map;
    for (const [id, ts] of entries) {
      if (id != null && typeof ts === 'number') map.set(String(id), ts);
    }
  } catch {
    // Ignore malformed cache.
  }
  return map;
}

function persistAddedAt() {
  try {
    localStorage.setItem(ADDED_AT_KEY, JSON.stringify([...addedAtMap]));
  } catch {
    // Quota / privacy mode — fail silently.
  }
}

/* ────────────────── Heart hydration / click ────────────────── */

function findAllHearts() {
  const out = [];
  function recurse(root) {
    if (!root.querySelectorAll) return;
    out.push(...root.querySelectorAll('[data-wishlist-btn]'));
    root.querySelectorAll('*').forEach((el) => {
      if (el.shadowRoot) recurse(el.shadowRoot);
    });
  }
  recurse(document);
  return out;
}

function applyHeartState(btn) {
  const id = btn.dataset.productId;
  const handle = btn.dataset.productHandle;
  let isFav = false;
  if (id && favoriteIds.has(String(id))) isFav = true;
  else if (handle && favoriteHandles.has(handle)) isFav = true;
  btn.classList.toggle('is-wishlisted', isFav);
  btn.setAttribute('aria-pressed', isFav ? 'true' : 'false');
  const title = btn.dataset.productTitle || '';
  const suffix = title ? ' — ' + title : '';
  btn.setAttribute('aria-label', (isFav ? 'Remove from wishlist' : 'Add to wishlist') + suffix);
}

function hydrateAll() {
  findAllHearts().forEach(applyHeartState);
  updateHeaderCount();
}

// Reflect the favorite count in the header heart icon's badge. Lives on
// every page (the header is global), driven entirely by favoriteIds — the
// cross-page source of truth. Empty string → hidden via the :empty CSS rule.
function updateHeaderCount() {
  if (!headerCountEl) return;
  const n = favoriteIds.size;
  headerCountEl.textContent = n === 0 ? '' : n < 100 ? String(n) : '99+';
}

function scheduleHydrate() {
  clearTimeout(hydrateTimer);
  hydrateTimer = setTimeout(hydrateAll, 100);
}

function onHeartClick(event) {
  const path = event.composedPath();
  let btn = null;
  for (const el of path) {
    if (el && el.matches && el.matches('[data-wishlist-btn]')) {
      btn = el;
      break;
    }
  }
  if (!btn) return;
  debug('[Wishlist] heart click captured', {
    productId: btn.dataset.productId,
    handle: btn.dataset.productHandle,
  });
  event.preventDefault();
  event.stopPropagation();
  // Stop other handlers (like Relewise's link navigation on the parent <a>)
  // from also reacting to this click.
  if (typeof event.stopImmediatePropagation === 'function') {
    event.stopImmediatePropagation();
  }
  toggleFavorite(btn);
}

async function toggleFavorite(btn) {
  if (!currentUser || isAnonymous(currentUser)) {
    console.warn('[Wishlist] Cannot favorite as anonymous user.');
    return;
  }
  const handle = btn.dataset.productHandle || null;
  const id = await resolveProductId(btn);
  if (!id) {
    console.warn('[Wishlist] Could not determine product id for heart', btn);
    return;
  }

  const wasFav = favoriteIds.has(String(id));
  const willBeFav = !wasFav;
  debug('[Wishlist] toggle', { productId: id, handle, willBeFav });

  // First toggle marks this device as a wishlist user, so future page
  // loads know its hearts are worth hydrating (see bootstrap gating).
  markWishlistUser();

  // Optimistic flip — update both maps so the UI agrees with itself.
  if (willBeFav) {
    favoriteIds.add(String(id));
    if (handle) favoriteHandles.add(handle);
    addedAtMap.set(String(id), Date.now());
    persistAddedAt();
  } else {
    favoriteIds.delete(String(id));
    if (handle) favoriteHandles.delete(handle);
    addedAtMap.delete(String(id));
    persistAddedAt();
  }
  persistFavoritesCache();
  hydrateAll();

  try {
    await tracker.trackProductEngagement({
      user: currentUser,
      product: { productId: String(id) },
      engagement: { isFavorite: willBeFav },
    });
    debug('[Wishlist] trackProductEngagement OK', { productId: id, isFavorite: willBeFav });

    // On the wishlist page, removing a favorite should remove the card.
    if (grid && wasFav && !willBeFav) {
      const card = btn.closest('.tabbed-product-card[data-wishlist-product-id]');
      if (card) {
        card.style.transition = 'opacity 200ms ease';
        card.style.opacity = '0';
        setTimeout(() => {
          card.remove();
          const idx = wishlistCards.findIndex((c) => c.id === String(id));
          if (idx > -1) wishlistCards.splice(idx, 1);
          updateCount();
          if (!grid.querySelector('.tabbed-product-card[data-wishlist-product-id]')) showEmpty();
        }, 200);
      }
    }
  } catch (err) {
    console.error('[Wishlist] trackProductEngagement failed; reverting', err);
    if (wasFav) {
      favoriteIds.add(String(id));
      if (handle) favoriteHandles.add(handle);
    } else {
      favoriteIds.delete(String(id));
      if (handle) favoriteHandles.delete(handle);
    }
    persistFavoritesCache();
    hydrateAll();
  }
}

/* ────────────────── Wishlist page rendering ────────────────── */

async function renderWishlistPage() {
  if (favoriteIds.size === 0) {
    showEmpty();
    return;
  }

  try {
    const builder = new ProductSearchBuilder({ ...settings, user: currentUser, displayedAtLocation: 'Wishlist Page' })
      .setSelectedProductProperties({
        displayName: true,
        pricing: true,
        userEngagement: true,
        allData: true,
      })
      .pagination((p) => p.setPageSize(200).setPage(1))
      .filters((f) => f.addProductEngagementFilter({ isFavorite: true }));

    const response = await searcher.searchProducts(builder.build());
    const items = response?.results ?? [];
    if (items[0]) debug('[Wishlist] wishlist render — sample item:', items[0]);

    if (items.length === 0) {
      showEmpty();
      return;
    }

    const rendered = (await Promise.all(items.map(renderCardAsync))).filter(Boolean);

    if (!rendered.length) {
      showEmpty();
      return;
    }

    wishlistCards.length = 0;
    wishlistCards.push(...rendered);

    if (empty) empty.style.display = 'none';
    if (toolbarEl) toolbarEl.hidden = false;
    paintGrid();
    updateCount();
    hydrateAll();
  } catch (err) {
    console.error('[Wishlist] Failed to render favorites', err);
    grid.innerHTML =
      '<div class="wishlist-loading">Kunde inte ladda din önskelista. Försök igen.</div>';
  }
}

function paintGrid() {
  if (!grid) return;
  const sorted = sortCards(wishlistCards, sortEl?.value || 'recent');
  grid.innerHTML = sorted.map((c) => c.html).join('');
}

function sortCards(list, mode) {
  const out = list.slice();
  switch (mode) {
    case 'price-asc':
      out.sort((a, b) => (a.price ?? Infinity) - (b.price ?? Infinity));
      break;
    case 'price-desc':
      out.sort((a, b) => (b.price ?? -Infinity) - (a.price ?? -Infinity));
      break;
    case 'name':
      out.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      break;
    case 'recent':
    default:
      out.sort((a, b) => (b.addedAt ?? 0) - (a.addedAt ?? 0));
      break;
  }
  return out;
}

function updateCount() {
  if (!countEl) return;
  const n = wishlistCards.length;
  if (n === 0) {
    countEl.textContent = '';
    return;
  }
  const template = n === 1
    ? countEl.dataset.countOne || '%COUNT% item'
    : countEl.dataset.countOther || '%COUNT% items';
  countEl.textContent = `· ${template.replace('%COUNT%', n)}`;
}

// Recursively unwrap a Relewise DataValue down to a primitive or array of
// primitives. Verified shapes per the @relewise/client SDK type defs:
//
//   DataValue envelope:  { type, value, isCollection }
//   StringCollection:    { $type, $values: ['a', 'b'] }   ← dollar-prefixed
//   Multilingual:        { values: [{ language, text }] } ← `text`, not `value`
//
// Peels each layer until it hits a primitive. Multilingual entries are
// resolved by matching <html lang> first, then falling back to the first
// non-empty entry.
function unwrapDataValue(v) {
  if (v == null) return null;
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return v;
  if (Array.isArray(v)) {
    const unwrapped = v.map(unwrapDataValue).filter((x) => x != null && x !== '');
    return unwrapped.length ? unwrapped : null;
  }
  if (typeof v === 'object') {
    // Collection envelope: { $type, $values: [...] }
    if (Array.isArray(v.$values)) return unwrapDataValue(v.$values);
    // Multilingual envelope: { values: [{ language, text }] }
    if (Array.isArray(v.values) && v.values.length && 'text' in v.values[0]) {
      const docLang = (document.documentElement.lang || '').toLowerCase();
      const match = v.values.find((e) => {
        const lang = e?.language?.value || e?.language || '';
        return String(lang).toLowerCase() === docLang && e?.text;
      });
      const entry = match || v.values.find((e) => e?.text) || v.values[0];
      return unwrapDataValue(entry?.text);
    }
    // Older-style list envelope { values: [...] } without `text` markers.
    if (Array.isArray(v.values)) return unwrapDataValue(v.values);
    // DataValue envelope: { type, value, isCollection } — peel `value`.
    if ('value' in v) return unwrapDataValue(v.value);
  }
  return null;
}

// Find the first data field whose key matches one of the candidate suffixes.
// Multi-store Relewise installs prefix keys with the shop domain
// (e.g. `2xu-nordics.myshopify.com_ShopifyHandle`), so we match by suffix.
// Returns the unwrapped scalar (or first array entry).
function readDataBySuffix(product, suffixes) {
  if (!product?.data) return null;
  const keys = Object.keys(product.data);
  for (const suffix of suffixes) {
    for (const key of keys) {
      if (key === suffix || key.endsWith('_' + suffix)) {
        const unwrapped = unwrapDataValue(product.data[key]);
        if (unwrapped == null || unwrapped === '') continue;
        if (Array.isArray(unwrapped)) {
          if (unwrapped.length) return unwrapped[0];
          continue;
        }
        return unwrapped;
      }
    }
  }
  return null;
}

let dataKeysLogged = false;

async function renderCardAsync(product) {
  const id = String(product.productId);

  // Read what we can from Relewise. Multi-store installs prefix keys with
  // the shop domain, so we match by suffix.
  let title =
    pickString(product.displayName) ||
    readDataBySuffix(product, ['Title', 'DisplayName', 'Name']) ||
    '';

  const relewiseImage =
    readDataBySuffix(product, ['ImageUrls', 'Images', 'ImageUrl', 'Image']) ||
    null;

  let handle = readDataBySuffix(product, ['ShopifyHandle', 'Handle']);
  if (!handle) {
    const urlField = readDataBySuffix(product, ['Url', 'PageUrl', 'ProductUrl']);
    if (urlField) handle = handleFromUrl(urlField);
  }

  if (!dataKeysLogged && product?.data) {
    dataKeysLogged = true;
    debug('[Wishlist] data inspection (first item):',
      '\n  data keys:', JSON.stringify(Object.keys(product.data)),
      '\n  displayName:', JSON.stringify(product.displayName),
      '\n  --- resolved ---',
      '\n  title:', JSON.stringify(title),
      '\n  image:', JSON.stringify(relewiseImage),
      '\n  handle:', JSON.stringify(handle)
    );
  }

  // Fetch the full Shopify product to get title (fallback), variant data
  // for the size buttons, and the secondary image used on hover.
  let shopifyProduct = null;
  if (handle) {
    try {
      const res = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
      if (res.ok) shopifyProduct = await res.json();
    } catch (err) {
      debug('[Wishlist] Failed to fetch Shopify product for', handle, err);
    }
  }

  if (shopifyProduct) {
    if (!title) title = shopifyProduct.title || '';
    if (!handle && shopifyProduct.handle) handle = shopifyProduct.handle;
  }

  if (!title && !relewiseImage && !shopifyProduct) {
    console.warn('[Wishlist] Skipping product with no usable data:', { productId: id });
    return null;
  }

  const sale = product.salesPrice;
  const list = product.listPrice;
  const url = handle ? `/products/${handle}` : '#';

  const primaryImage = shopifyProduct?.featured_image || shopifyProduct?.images?.[0] || relewiseImage;
  const hoverImage = shopifyProduct?.images?.[1] || null;
  const priceSale = sale != null ? sale : (shopifyProduct ? shopifyProduct.price / 100 : null);
  const priceList = list != null ? list : (shopifyProduct && shopifyProduct.compare_at_price ? shopifyProduct.compare_at_price / 100 : null);
  const onSale = priceList != null && priceSale != null && priceList > priceSale;
  const addedAt = addedAtMap.get(id) || 0;

  const html = buildCardHTML({
    id,
    title,
    url,
    handle,
    primaryImage,
    hoverImage,
    onSale,
    priceSale,
    priceList,
    sizesHTML: shopifyProduct ? buildSizesHTML(shopifyProduct) : '',
  });

  return { id, title, price: priceSale ?? 0, addedAt, html, handle };
}

function buildCardHTML({ id, title, url, handle, primaryImage, hoverImage, onSale, priceSale, priceList, sizesHTML }) {
  const titleSafe = title || '';
  const handleAttr = handle ? ` data-product-handle="${escapeAttr(handle)}"` : '';
  return (
    `<div class="tabbed-product-card" data-wishlist-product-id="${escapeAttr(id)}">` +
      `<button type="button" class="wishlist-heart is-wishlisted" data-wishlist-btn ` +
        `data-product-id="${escapeAttr(id)}"${handleAttr} ` +
        `data-product-title="${escapeAttr(titleSafe)}" ` +
        `aria-label="Remove ${escapeAttr(titleSafe)} from wishlist" aria-pressed="true">` +
        '<span class="wishlist-heart__icon wishlist-heart__icon--outline">' + heartOutlineSVG() + '</span>' +
        '<span class="wishlist-heart__icon wishlist-heart__icon--filled">' + heartFilledSVG() + '</span>' +
      '</button>' +
      `<a href="${escapeAttr(url)}" class="tabbed-product-card__link">` +
        '<div class="tabbed-product-card__media">' +
          (primaryImage
            ? `<img class="tabbed-product-card__img tabbed-product-card__img--primary" ` +
                `src="${escapeAttr(sizedImage(primaryImage, 720))}" ` +
                `alt="${escapeAttr(titleSafe)}" loading="lazy">`
            : '<div class="tabbed-product-card__placeholder"></div>') +
          (hoverImage
            ? `<img class="tabbed-product-card__img tabbed-product-card__img--hover" ` +
                `src="${escapeAttr(sizedImage(hoverImage, 720))}" ` +
                `alt="" loading="lazy">`
            : '') +
          (sizesHTML ? `<div class="tabbed-product-card__sizes">${sizesHTML}</div>` : '') +
        '</div>' +
      '</a>' +
      '<div class="tabbed-product-card__info">' +
        `<a href="${escapeAttr(url)}" class="tabbed-product-card__title-link">` +
          `<h3 class="tabbed-product-card__title">${escapeHTML(titleSafe)}</h3>` +
          '<p class="tabbed-product-card__price">' +
            formatMoney(priceSale) +
            (onSale ? ` <span class="tabbed-product-card__price--compare">${formatMoney(priceList)}</span>` : '') +
          '</p>' +
        '</a>' +
      '</div>' +
    '</div>'
  );
}

function buildSizesHTML(shopifyProduct) {
  if (!shopifyProduct?.options || !shopifyProduct?.variants) return '';
  let sizeIndex = -1;
  shopifyProduct.options.forEach((opt, i) => {
    const name = typeof opt === 'string' ? opt : opt?.name;
    if (name && name.toLowerCase() === 'size') sizeIndex = i;
  });
  if (sizeIndex < 0) return '';

  const seen = new Set();
  const buttons = [];
  for (const v of shopifyProduct.variants) {
    const sizeVal = v.options?.[sizeIndex];
    if (!sizeVal || seen.has(sizeVal)) continue;
    seen.add(sizeVal);

    let pickedVariant = v;
    let available = false;
    for (const vv of shopifyProduct.variants) {
      if (vv.options?.[sizeIndex] === sizeVal && vv.available) {
        available = true;
        pickedVariant = vv;
        break;
      }
    }

    buttons.push(
      `<button type="button" class="tabbed-product-card__size-btn` +
        (available ? '' : ' tabbed-product-card__size-btn--unavailable') + `" ` +
        `data-variant-id="${escapeAttr(pickedVariant.id)}" ` +
        `data-size-label="${escapeAttr(sizeVal)}" ` +
        (available ? '' : 'disabled ') +
        `aria-label="Add ${escapeAttr(sizeVal)} to cart">` +
        escapeHTML(sizeVal) +
      '</button>'
    );
  }
  return buttons.join('');
}

function pickString(value) {
  // Reuse the canonical unwrap so Multilingual { values: [{ language, text }] }
  // and DataValue envelopes resolve consistently.
  const out = unwrapDataValue(value);
  if (out == null) return '';
  if (Array.isArray(out)) return String(out[0] ?? '');
  return String(out);
}

function formatMoney(amount) {
  if (amount == null) return '';
  try {
    return formatter.format(amount);
  } catch {
    return String(amount);
  }
}

function showEmpty() {
  if (grid) grid.innerHTML = '';
  if (toolbarEl) toolbarEl.hidden = true;
  if (countEl) countEl.textContent = '';
  if (empty) empty.style.display = '';
}

/* ────────────────── Add-to-cart from card ────────────────── */

function onSizeButtonClick(event) {
  const btn = event.target.closest('.tabbed-product-card__size-btn');
  if (!btn || !grid.contains(btn)) return;

  event.preventDefault();
  event.stopPropagation();

  const variantId = btn.getAttribute('data-variant-id');
  if (!variantId || btn.disabled) return;

  btn.classList.add('tabbed-product-card__size-btn--adding');
  btn.textContent = '...';

  const cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  const body = { items: [{ id: parseInt(variantId, 10), quantity: 1 }] };
  if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
    body.sections = cartEl.getSectionsToRender().map((s) => s.id);
    body.sections_url = window.location.pathname;
  }

  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
    .then((res) => {
      if (!res.ok) throw new Error('Add to cart failed');
      return res.json();
    })
    .then((response) => {
      btn.textContent = '✓';
      if (cartEl && typeof cartEl.renderContents === 'function') {
        cartEl.renderContents(response);
      }
      setTimeout(() => {
        btn.classList.remove('tabbed-product-card__size-btn--adding');
        btn.textContent = btn.getAttribute('data-size-label');
      }, 1200);
    })
    .catch(() => {
      btn.classList.remove('tabbed-product-card__size-btn--adding');
      btn.textContent = '!';
      setTimeout(() => {
        btn.textContent = btn.getAttribute('data-size-label');
      }, 1200);
    });
}

/* ────────────────── Toolbar (sort / share / clear) ────────────────── */

function bindToolbar() {
  if (sortEl) {
    sortEl.addEventListener('change', () => paintGrid());
  }

  if (shareBtn) {
    shareBtn.addEventListener('click', async () => {
      const shareUrl = window.location.href;
      const shareData = {
        title: 'My 2XU Wishlist',
        text: 'Check out my 2XU wishlist',
        url: shareUrl,
      };
      try {
        if (navigator.share) {
          await navigator.share(shareData);
        } else if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(shareUrl);
          showToast('Länk kopierad till urklipp');
        }
      } catch (err) {
        debug('[Wishlist] share cancelled / failed', err);
      }
    });
  }

  if (clearBtn) {
    clearBtn.addEventListener('click', async () => {
      if (!wishlistCards.length) return;
      const confirmed = window.confirm('Vill du ta bort alla objekt från din önskelista?');
      if (!confirmed) return;
      await clearAllFavorites();
    });
  }
}

async function clearAllFavorites() {
  const ids = wishlistCards.map((c) => c.id);
  // Optimistic UI first.
  wishlistCards.length = 0;
  for (const id of ids) {
    favoriteIds.delete(String(id));
    addedAtMap.delete(String(id));
  }
  favoriteHandles.clear();
  persistAddedAt();
  showEmpty();
  hydrateAll();

  // Track removal in Relewise. Fire-and-forget — failures will resync next load.
  await Promise.all(
    ids.map((id) =>
      tracker
        .trackProductEngagement({
          user: currentUser,
          product: { productId: String(id) },
          engagement: { isFavorite: false },
        })
        .catch((err) => console.error('[Wishlist] clear-all engagement failed', id, err))
    )
  );
  showToast('Önskelistan rensad');
}

let toastTimer = null;
function showToast(message) {
  if (!toastEl) return;
  toastEl.textContent = message;
  toastEl.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    toastEl.hidden = true;
  }, 2400);
}

/* ────────────────── User helpers ────────────────── */

async function getUser(timeoutMs = 8000) {
  // The Relewise app embed loads asynchronously and may not have
  // populated window.ShopifyRelewise yet by the time bootstrap runs.
  // Poll until it appears or we hit the timeout.
  const start = Date.now();
  while (
    !window.ShopifyRelewise ||
    typeof window.ShopifyRelewise.getUserAsync !== 'function'
  ) {
    if (Date.now() - start > timeoutMs) {
      console.warn(
        '[Wishlist] window.ShopifyRelewise.getUserAsync never became available (timeout ' +
          timeoutMs +
          'ms). Is the Relewise app embed enabled?'
      );
      return null;
    }
    await new Promise((r) => setTimeout(r, 50));
  }
  try {
    return await window.ShopifyRelewise.getUserAsync();
  } catch (err) {
    console.error('[Wishlist] ShopifyRelewise.getUserAsync threw', err);
    return null;
  }
}

function isAnonymous(user) {
  if (!user) return true;
  if (user.authenticatedId) return false;
  if (user.temporaryId) return false;
  if (user.identifiers && Object.keys(user.identifiers).length > 0) return false;
  return true;
}

/* ────────────────── DOM / formatting helpers ────────────────── */

function readData(product, key) {
  const v = product?.data?.[key];
  if (v == null) return null;
  return v.value ?? v;
}

function sizedImage(url, width) {
  if (!url || typeof url !== 'string') return url;
  if (url.includes('cdn.shopify.com')) {
    return url.includes('?') ? `${url}&width=${width}` : `${url}?width=${width}`;
  }
  return url;
}

function escapeHTML(str) {
  const div = document.createElement('div');
  div.textContent = str == null ? '' : String(str);
  return div.innerHTML;
}

function escapeAttr(str) {
  return String(str == null ? '' : str)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function heartFilledSVG() {
  return (
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" aria-hidden="true">' +
      '<path d="M10 17.495l-7.695-7.593a4.293 4.293 0 0 1 0-6.128 4.433 4.433 0 0 1 6.21 0L10 5.24l1.485-1.466a4.433 4.433 0 0 1 6.21 0 4.293 4.293 0 0 1 0 6.128L10 17.495z"/>' +
    '</svg>'
  );
}

function heartOutlineSVG() {
  return (
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">' +
      '<path d="M10 17.495l-7.695-7.593a4.293 4.293 0 0 1 0-6.128 4.433 4.433 0 0 1 6.21 0L10 5.24l1.485-1.466a4.433 4.433 0 0 1 6.21 0 4.293 4.293 0 0 1 0 6.128L10 17.495z"/>' +
    '</svg>'
  );
}
